import { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  IconButton,
  Divider,
  Typography,
} from "@mui/material";
import { nanoid } from 'nanoid';

import {
  accordionBody,
  accordionHeaderText,
  dividerMargin,
  errorHeaderBox,
  errorLabelBox,
  FaChevronCircleDownIcon,
  FaChevronCircleUpIcon,
} from "../styles/Review";
import { TabSessionStorageResponse } from "../../../models/api/TabSessionStorage";
import AgencyDetails from "./Review/AgencyDetails";
import ReferralSource from "./Review/ReferralSource";
import PhysicianDetails from "./Review/PhysicianDetails";
import ICD10 from "./Review/ICD10";
import colors from "../../../styles/colors/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import DocumentDetails from "./Review/DocumentUpload";
import { TypographyBox } from "../../../app/common/TextLabel";
import { notVisitedTabs } from "../../../models/TabSessionStorage";
import RequestServices from "./Review/RequestServices";
import { Discipline, ListOptions } from "../../../models/Master";

export interface PropsFromState {
  tabSessionStorage: TabSessionStorageResponse;
  files: File[];
  masterCareTypeState: ListOptions[];
  disciplineListState: Discipline[];
  masterSendCommunicationAuthTypeState: ListOptions[];
}

const Review: React.FC<PropsFromState> = ({ tabSessionStorage, files, masterCareTypeState, disciplineListState, masterSendCommunicationAuthTypeState }) => {

  const accordionData = [
    {
      title: "Agency Details",
      content: <AgencyDetails tabSessionStorage={tabSessionStorage} masterSendCommunicationAuthTypeState={masterSendCommunicationAuthTypeState}/>,
    },
    {
      title: "Referral Source",
      content: <ReferralSource tabSessionStorage={tabSessionStorage} />,
    },
    {
      title: "Physician Detail",
      content: <PhysicianDetails tabSessionStorage={tabSessionStorage} />,
    },
    { title: "ICD 10", content: <ICD10 tabSessionStorage={tabSessionStorage} /> },
    {
      title: "Request Services",
      content: <RequestServices tabSessionStorage={tabSessionStorage} masterCareTypeState={masterCareTypeState} disciplineListState={disciplineListState} />
    },
    {
      title: "Document Upload",
      content: <DocumentDetails tabSessionStorage={tabSessionStorage} files={files} />,
    },
  ];

  const [expanded, setExpanded] = useState<string[]>(accordionData.map((_, index) => `panel${index}`));

  const onChangeHandler = (isExpanded: boolean, index: number) => {
    return setExpanded((prev) => {
      if (!Array.isArray(prev)) prev = [];
      if (isExpanded) {
        return [...prev, `panel${index}`];
      } else {
        return prev.filter((panel: string) => panel !== `panel${index}`);
      }
    });
  };

  const isExpanded = (expanded: string[], index: number) => {
    return Array.isArray(expanded)
      ? expanded.includes(`panel${index}`)
      : expanded === `panel${index}`;
  };

  const icon = (index: number) => {
    return (
      <IconButton edge="start" disableRipple>
        {Array.isArray(expanded) && expanded.includes(`panel${index}`) ? (
          <FaChevronCircleUpIcon />
        ) : (
          <FaChevronCircleDownIcon />
        )}
      </IconButton>
    );
  };

  return (
    <Box>
      {accordionData.map((item, index) => (
        <Box key={nanoid()}>
          <Accordion
            expanded={isExpanded(expanded, index)}
            onChange={(_event, isExpanded) =>
              onChangeHandler(isExpanded, index)
            }
          >
            <AccordionSummary
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
            >
              {icon(index)}
              <Box sx={accordionHeaderText}>
                {TypographyBox(item.title, colors.fonts[20], fontWeight[5])}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={accordionBody}>{item.content}</AccordionDetails>
          </Accordion>
          {index < accordionData.length - 1 && (
            <Divider sx={dividerMargin} />
          )}
        </Box>
      ))}
      {notVisitedTabs(tabSessionStorage).length > 0 && (
        <Box m={2} pb={2}>
          <Box
            sx={errorHeaderBox}
          >
            <Typography variant="h6" fontWeight={fontWeight[4]} color={colors.red[100]}>
                Please complete the following tabs:
            </Typography>
          </Box>
          {notVisitedTabs(tabSessionStorage).map((tab) => (
            <Box
              key={nanoid()}
              sx={errorLabelBox}
            >
              {tab.label}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Review;
